// Sends a page -> app message over whichever channel the host provides:
// - Native app (react-native-webview): window.ReactNativeWebView.postMessage
// - Web app (embedded in a cross-origin iframe): window.parent.postMessage —
//   the parent cannot observe messages posted on this frame's own window, so
//   posting to the parent (with targetOrigin "*") is required for the web app
//   to receive anything. The payload is non-sensitive page-render data.
// - Standalone (no host): self window.postMessage, preserving old behavior.
export function send(payload) {
  if (window["ReactNativeWebView"]) {
    window["ReactNativeWebView"].postMessage(payload);
    return;
  }
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(payload, "*");
    return;
  }
  window.postMessage(payload);
}

export function postMessage(question) {
  send(JSON.stringify(question));
}

export function postImageQualityWarning(
  recommendedWidthPixels,
  recommendedHeightPixels,
  width,
  height
) {
  const message = {
    type: "imageQualityWarning",
    recommendedWidthPixels,
    recommendedHeightPixels,
    width,
    height
  };

  send(JSON.stringify(message));
}

export function postReady() {
  const message = {
    type: "ready"
  };

  send(JSON.stringify(message));
}

// Tells the host a page payload has been rendered (layout painted; photos
// may still stream in). The native app uses this to lift the swap cover on
// recycled list webviews, matching on the page id to ignore stale acks.
export function postPageRendered(id) {
	const message = {
		type: "pageRendered",
		id
	};

	send(JSON.stringify(message));
}

export function postOverflow(overflow) {
  const message = {
    type: "overflow",
    overflow
  };

  send(JSON.stringify(message));
}

export function postOverflowWarning() {
  const message = {
    type: "overflowWarning"
  };

  send(JSON.stringify(message));
}

export function postImageLoadingError(imgObject) {
  const message = {
    type: "imageLoadingError",
    imgObject
  };

  send(JSON.stringify(message));
}

export function postCoachmarkRect(target, rect, viewportWidth) {
  const message = {
    type: "coachmark-rect",
    target,
    rect,
    viewportWidth
  };

  send(JSON.stringify(message));
}

export function postEditableRects(items) {
  const message = {
    type: "editable-rects",
    items
  };

  send(JSON.stringify(message));
}

// Drag-and-drop of photos between slots (view=preview only). The source
// frame announces the drag lifecycle; the frame that receives the drop —
// possibly the OTHER page's iframe — posts the swap request.
export function postPhotoDragStart(questionId, imgSrc) {
  const message = {
    type: "photo-drag-start",
    questionId,
    // The dragged image's current source, so the HOST can draw the drag
    // ghost (the cooperative pointer drag has no native ghost).
    imgSrc: imgSrc || null
  };

  send(JSON.stringify(message));
}

export function postPhotoDrop(fromQuestionId, toQuestionId) {
  const message = {
    type: "photo-drop",
    fromQuestionId,
    toQuestionId
  };

  send(JSON.stringify(message));
}

// The host receives NO drag events at all while a drag is sourced in this
// (cross-process) iframe — verified live: the embedding page sees neither
// dragenter/dragover nor drop for the whole gesture. The source element's
// own `drag` event keeps firing with SCREEN coordinates regardless of what
// is under the cursor, so the host can hit-test its rail/arrows from this
// stream and resolve drops on release.
export function postPhotoDragMove(clientX, clientY) {
  // clientX/Y are THIS document's viewport coordinates (the fixed 1000px
  // space) — they stay linearly valid even when the cursor is outside the
  // iframe, and the host maps them through the pane's on-screen rect. That
  // conversion is immune to window chrome, DevTools and zoom, unlike
  // screen-coordinate math.
  const message = {
    type: "photo-drag-move",
    clientX,
    clientY
  };

  send(JSON.stringify(message));
}

// Pointer released while over THIS document: the host resolves the drop
// against its drawn slot rects (panes and rail alike) — the preview is
// never an interactive drop surface, only a pointer reporter.
export function postPhotoDragRelease(clientX, clientY) {
  const message = {
    type: "photo-drag-release",
    clientX,
    clientY
  };

  send(JSON.stringify(message));
}

export function postPhotoDragEnd() {
  const message = {
    type: "photo-drag-end"
  };

  send(JSON.stringify(message));
}
